import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {Link, useNavigate} from 'react-router-dom';
import { PackCombinationDisplay } from '../utils/PackCombDisplay';
import useCart from '../components/hooks/hooks-e-commerce/useCart';
import useAuth from '../components/hooks/hooks-e-commerce/useAuth';
import { useLoginModal } from '../contexts/LoginModalContext';
import { authEventBus, AUTH_EVENTS } from '../utils/AuthEvents';
import Shopping from '../components/images/icons/ShopW64.png';
import iconInfo from '../shop/components/images/iconstore/iconInfOrW38.png';
import iconPagSecuroBlk from '../components/images/icons/iconPagSecuroBlk50.png';
import iconDeliveryBlk from '../components/images/icons/iconDeliveryBlackW60.png';
import iconPackagingBlk from '../components/images/icons/iconPackagingW48.png';
import iconRemoved from '../shop/components/images/iconstore/iconDeleteOrW28.png';
import './cart.scss';

function Cart() {
    const { 
        error, 
        loading, 
        cart, 
        removeCartItem,
        refreshCart,
        updatePackQuantities
    } = useCart();

    const { user, isAuthenticated } = useAuth();

    const { openLoginModal } = useLoginModal(); 

    const navigate = useNavigate();

    const SHIPPING_COST_PER_BOTTLE = 3.40;

    const [removingItemId, setRemovingItemId] = useState(null);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [pendingOrder, setPendingOrder] = useState(false);
    const [redirectAttempted, setRedirectAttempted] = useState(false);
    const [expandedItems, setExpandedItems] = useState({});
    
    const items = useMemo(() => cart?.items || [], [cart?.items]);
    //console.log('📦 = Nel carrello: ', cart.items);

    // Gestione pending order e reindirizzamento
    useEffect(() => {
        const hasPendingInStorage = sessionStorage.getItem('pendingCheckout') === 'true';
        
        if (hasPendingInStorage && !pendingOrder && !isAuthenticated) {
            setPendingOrder(true);
        }

        if (isAuthenticated && (pendingOrder || hasPendingInStorage) && !redirectAttempted) {
            setRedirectAttempted(true);
            setPendingOrder(false);
            
            sessionStorage.removeItem('pendingCheckout');
            sessionStorage.removeItem('pendingCartId');
            sessionStorage.removeItem('pendingCartData');
            
            if (typeof refreshCart === 'function') {
                refreshCart();
            }
        }

        if (!isAuthenticated && !user) {
            setPendingOrder(false);
            setRedirectAttempted(false);
            sessionStorage.removeItem('pendingCheckout');
            sessionStorage.removeItem('pendingCartId');
            sessionStorage.removeItem('pendingCartData');
        }
    }, [isAuthenticated, user, pendingOrder, redirectAttempted, refreshCart]);

    // Ascolta evento di logout
    useEffect(() => {
        const handleLogout = () => {
            setPendingOrder(false);
            setRedirectAttempted(false);
            setShowSuccessMessage(false);
            sessionStorage.removeItem('pendingCheckout');
            sessionStorage.removeItem('pendingCartId');
            sessionStorage.removeItem('pendingCartData');
            refreshCart();
        };
        
        const unsubscribe = authEventBus.subscribe(AUTH_EVENTS.LOGOUT, handleLogout);
        return () => unsubscribe();
    }, [refreshCart]);

    
    // Ascolta eventi di reset/refresh carrello
    useEffect(() => {
        const handleCartReset = () => refreshCart();
        const handleCartRefresh = () => refreshCart();
        
        window.addEventListener('cart:reset', handleCartReset);
        window.addEventListener('cart:refresh', handleCartRefresh);
        
        return () => {
            window.removeEventListener('cart:reset', handleCartReset);
            window.removeEventListener('cart:refresh', handleCartRefresh);
        };
    }, [refreshCart]);

    // Verifica autenticazione e reindirizzamento
    useEffect(() => {
        const isUserLoggedIn = isAuthenticated || cart?.id_utente;
        const hasPendingInStorage = sessionStorage.getItem('pendingCheckout') === 'true';
        
        // ✅ Mostra un messaggio di benvenuto dopo il login
        if (isUserLoggedIn && hasPendingInStorage && !redirectAttempted) {
            // Mostra messaggio di successo
            setSuccessMessage(`✅ Benvenuto ${user?.nome_utente || user?.email || ''}! Ora puoi completare il tuo ordine.`);
            setShowSuccessMessage(true);
            
            // Attendi 1.5 secondi prima del redirect (per dare visibilità al messaggio)
            setTimeout(() => {
                setRedirectAttempted(true);
                setPendingOrder(false);
                
                sessionStorage.removeItem('pendingCheckout');
                sessionStorage.removeItem('pendingCartId');
                sessionStorage.removeItem('pendingCartData');
                
                // ✅ REINDIRIZZA AL CHECKOUT
                navigate('/negozioonline/checkout/ordinidiretti');
                
                // Nascondi il messaggio dopo il redirect
                setTimeout(() => setShowSuccessMessage(false), 2000);
            }, 1500);
        }
    }, [isAuthenticated, cart?.id_utente, redirectAttempted, pendingOrder, user, navigate]);

    // Listener per l'evento di login
    useEffect(() => {
        const handleLoginSuccess = () => {
            console.log('✅ Login rilevato, controllo pending checkout...');
            const hasPending = sessionStorage.getItem('pendingCheckout') === 'true';
            
            if (hasPending) {
                setSuccessMessage('✅ Login effettuato! Reindirizzamento al checkout...');
                setShowSuccessMessage(true);
                
                setTimeout(() => {
                    navigate('/negozioonline/checkout/ordinidiretti');
                    sessionStorage.removeItem('pendingCheckout');
                    sessionStorage.removeItem('pendingCartId');
                    sessionStorage.removeItem('pendingCartData');
                }, 1000);
            }
        };
        
        // Iscriviti all'evento di login (usa il tuo event bus)
        const unsubscribe = authEventBus.subscribe(AUTH_EVENTS.LOGIN, handleLoginSuccess);
        
        return () => unsubscribe();
    }, [navigate]);

    const getTotalBottles = useCallback(() => {
        return items.reduce((total, item) => total + (item.quantita || 0), 0);
    }, [items]);

    const calculateShippingCost = useCallback(() => {
        return getTotalBottles() * SHIPPING_COST_PER_BOTTLE;
    }, [getTotalBottles]);

    const totals = useMemo(() => {
        const subtotal = items.reduce((sum, item) => {
            const price = item.prezzo_unitario || 0;
            const quantity = item.quantita || 0;
            const calc = price * quantity;
            //console.log(`📊 Item ${item.id}: €${price} × ${quantity} = €${calc}`);
            return sum + calc;
        }, 0);

        const packagingTotal = items.reduce((total, item) => {
        let cost = 0;
        if (item.packaging?.packagingCost) {
            cost = Number(item.packaging.packagingCost);
        } else if (item.packaging?.cost) {
            cost = Number(item.packaging.cost);
        } else if ([3, 6, 12].includes(item.quantita)) {
            const PACKAGING_COSTS = { 3: 1.50, 6: 2.50, 12: 3.50 };
            cost = PACKAGING_COSTS[item.quantita] || 0;
        }
        return total + cost;
    }, 0);
    
    const shippingCost = calculateShippingCost();
    const totalBottles = getTotalBottles();
        
        return {
            subtotal,
            packagingTotal,
            shippingCost,
            total: subtotal + packagingTotal + shippingCost,
            totalBottles
        };
    }, [items, calculateShippingCost, getTotalBottles]);

    const toggleExpand = (itemId) => {
        setExpandedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    const getIvaAliquota = (item) => {
        return item.aliquota_iva || 22.00;
    };


    const ivaCalculation = useMemo(() => {
        let ivaProdotti = 0;
        let imponibileProdotti = 0;
        
        items.forEach(item => {
            const aliquota = getIvaAliquota(item);
            const imponibile = (item.prezzo_unitario || 0) * (item.quantita || 0);
            imponibileProdotti += imponibile;
            ivaProdotti += imponibile * (aliquota / 100);
        });
        
        // Calcolo IVA imballaggio (usa l'aliquota più alta dei prodotti)
        let maxAliquota = 22;
        items.forEach(item => {
            const aliquota = getIvaAliquota(item);
            if (aliquota > maxAliquota) maxAliquota = aliquota;
    });

        const imponibileImballaggio = totals.packagingTotal;
        const ivaImballaggio = imponibileImballaggio * (maxAliquota / 100);
        
        // Calcolo IVA spedizione (usa l'aliquota più alta dei prodotti)
        const imponibileSpedizione = totals.shippingCost;
        const ivaSpedizione = imponibileSpedizione * (maxAliquota / 100);
        
        // Totale IVA
        const totaleIva = ivaProdotti + ivaImballaggio + ivaSpedizione;
        
        return {
            ivaProdotti,
            ivaImballaggio,
            ivaSpedizione,
            totaleIva,
            maxAliquota,
            imponibileProdotti,
            imponibileImballaggio,
            imponibileSpedizione
        };
    }, [items, totals.packagingTotal, totals.shippingCost]);

    const handlerPreviewPage = () => {
        navigate(-1);
    };

    // Funzione handleCompleteOrder:
    const handleCompleteOrder = () => {
        const isUserLoggedIn = isAuthenticated || cart?.id_utente;
        
        if (!isUserLoggedIn) {
            sessionStorage.setItem('pendingCheckout', 'true');
            sessionStorage.setItem('pendingCartId', cart?.id);
            sessionStorage.setItem('pendingCartData', JSON.stringify(cart));

            setSuccessMessage('🔐 Per completare l\'ordine, effettua il login o crea un account');
            setShowSuccessMessage(true);
            setTimeout(() => setShowSuccessMessage(false), 3000);

            // 🔥 APRE IL MODALE DI LOGIN usando il provider centralizzato
            openLoginModal('login');
        } else {
            navigate('/negozioonline/checkout/ordinidiretti');
        }
    };

    const handleRemoveItem = async (itemId, itemName) => {
        try {
            setRemovingItemId(itemId);
            const result = await removeCartItem(itemId);
            
            if (result.success) {
                setSuccessMessage(`${itemName} rimosso dal carrello`);
                setShowSuccessMessage(true);
                setTimeout(() => setShowSuccessMessage(false), 3000);
            }
        } catch (err) {
            console.error('Errore rimozione:', err);
        } finally {
            setRemovingItemId(null);
        }
    };

    const handlePackSave = async (itemId, packQuantities, totalQuantity) => {
        const result = await updatePackQuantities(itemId, packQuantities, totalQuantity);
        if (!result.success) {
            console.error('❌ Errore aggiornamento:', result.error);
        }
    };

    const deliveryDate = useMemo(() => {
        const date = new Date();
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate() + 2, 3, 0, 0));
    }, []);

    if (loading) return <div className="cart-loading">Caricamento carrello...<span className="loggin-spinner">⏳</span></div>;
    if (error) return <div className="error">Errore: {error}</div>;

    if (!cart || items.length === 0) {
        return (
            <div className='container-cart__empty realistic'>
                <div className="cart-empty__titles">
                    <p className="cart-empty__title">Il tuo carrello è vuoto</p>
                    <p className='cart-empty__state'>Non hai ancora aggiunto prodotti al carrello.</p>
                </div>
                <div className="cart-empty__link">
                    <span>
                        <Link to="/negozioonline" ><img src={Shopping} alt="icon of cart" />
                        <p className='link__back'>Continua a fare acquisti</p></Link>
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className='container-cart'>
            {/* <div className="cart__link" onClick={handlerPreviewPage}>
                <img src={arrowLeft} alt="icon of arrow at left" />
                <p className='link__back'>Tornare in dietro</p>
            </div> */}
            
            {showSuccessMessage && (
                <div className="success-message realistic">
                    {successMessage}
                </div>
            )}
            
            <div className="cart__content">
                <div className="content__articles">
                    <div className="header-cart">
                        <img src={Shopping} alt="icon of cart" onClick={handlerPreviewPage} title='Continua a fare acquisti'/>
                        <p className="cart__title">Il tuo carrello</p>
                    </div>
                    
                    <div className="cart__promo realistic">
                        <img src={iconInfo} alt="icon of info" />
                        <div className="promo__info">
                            <p className='promo__text'><strong>Sconto del 50%</strong> sulle spese di spedizione</p>
                            <p className="promo__text ml-1"> per tutta la <strong>regione dell'Abruzzo.</strong></p>
                        </div>
                    </div>
                    
                    <div className="cart-items">
                        {items.map((elt) => {
                            return (
                                <div className="cart-article realistic" key={elt.id}>
                                    <div className="article__identity">
                                        <img 
                                            className='cart-article__img' 
                                            src={require(`../shop/components/images/cart/${elt.img_cartart}.png`)} 
                                            alt="wine bottle" 
                                        />
                                        <div className="article__infos">
                                            <p className="infos__cart">
                                                <strong>{elt.nome_cantina}</strong>
                                            </p>
                                            <p className="infos__cart">
                                                <strong>{elt.nome_prodotto}</strong> - {elt.denominazione}
                                            </p>
                                            <p className="infos__cart">
                                                <strong>{elt.tipo_vino}</strong> - {elt.annata}
                                            </p>
                                            <p className="infos__cart">
                                                {elt.vitigno?.vitigno_One}, {elt.vitigno?.vitigno_Two}
                                            </p>
                                            <p className="infos__cart">
                                                € {elt.prezzo_unitario.toFixed(2)}<strong> - Per unità</strong>
                                            </p>
                                            {elt.promo_attiva && (
                                            <p className="infos__cart promo">
                                                <span style={{textDecoration: 'line-through'}} ><strong>€ {elt.prezzo_base.toFixed(2)}</strong></span> - promo: <strong>{elt.promo_value}%</strong>
                                            </p>
                                            )}
                                        </div>
                                    </div>
                                    <PackCombinationDisplay 
                                        item={elt}
                                        onSave={handlePackSave}
                                        isExpanded={expandedItems[elt.id] || false}
                                        onToggleExpand={() => toggleExpand(elt.id)}
                                    />
                                    
                                    <div className="article__removed">
                                        <img
                                            className='removed__icon'
                                            onClick={() => handleRemoveItem(elt.id, elt.nome_prodotto)}
                                            src={iconRemoved}
                                            alt="icon for removed"
                                            title="Elimina articolo"
                                            style={{
                                                opacity: removingItemId === elt.id ? 0.5 : 1,
                                                cursor: removingItemId === elt.id ? 'wait' : 'pointer'
                                            }}
                                        />
                                        {removingItemId === elt.id && (
                                            <span className="removing-spinner">⏳</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                
                <div className="cart-summary">
                    <div className="summary-code__promo-btn realistic">
                        <div className="code__promo">
                            <p className="infos__cart">Hai un codice</p>
                            <p className="infos__cart">promozionale</p>
                            <p className="infos__cart">o professionale?</p>
                        </div>
                        <div className="btn__promo">
                            <button className="realistic">Applicare</button>
                        </div>
                    </div>
                    
                    <div className="price__resume realistic">
                        <p className="summary__title">Riepilogo</p>
                        
                        <div className="riepilogo-summary">
                            <p className="summary__subtotal">
                                Subtotale: <span>€ {totals.subtotal.toFixed(2)}</span>
                            </p>

                            {/* Dettaglio IVA prodotti (opzionale - espandibile) */}
                            {/* <div className="iva-detail">
                                <p className="summary__iva">
                                    <span>di cui IVA ({ivaCalculation.maxAliquota}%):</span>
                                    <span>€ {ivaCalculation.ivaProdotti.toFixed(2)}</span>
                                </p>
                            </div> */} 
                            
                            {totals.packagingTotal > 0 && (
                                <>
                                    <p className="summary__spese-extra">
                                        <span>Spese di confezionamento:</span>
                                        <span>+ € {totals.packagingTotal.toFixed(2)}</span>
                                    </p>
                                    {/*  <p className="summary__iva summary__iva-small">
                                        <span>IVA confezionamento ({ivaCalculation.maxAliquota}%):</span>
                                        <span>€ {ivaCalculation.ivaImballaggio.toFixed(2)}</span>
                                    </p> */}
                                </>
                            )}
                            
                            <p className="summary__spese-extra">
                                <span>Spese di consegna:</span> <span>+ € {totals.shippingCost.toFixed(2)}</span>
                            </p>
                            {/* <p className="summary__iva summary__iva-small">
                                <span>IVA consegna ({ivaCalculation.maxAliquota}%):</span>
                                <span>€ {ivaCalculation.ivaSpedizione.toFixed(2)}</span>
                            </p> */}
                            
                            <hr />
                            
                            <div className="summary-total">
                                <p className="summary__subtotal total">
                                    Totale (IVA inclusa): <span>€ {(totals.total).toFixed(2)}</span>
                                </p>
                                <p className="summary__iva-total">
                                    <span>IVA totale:</span>
                                    <span>€ {ivaCalculation.totaleIva.toFixed(2)}</span>
                                </p>
                            </div>
                        </div>
                        
                        <div className="link-summary__btn">
                            <button 
                                className="summary__btn mb-2 realistic"
                                onClick={handleCompleteOrder}
                            >
                                Completa l'ordine
                            </button>
                        </div>
                    </div>
                    
                    <div className="cart-delevery realistic">
                        <div className="delevery-termini mt-12">
                            <img src={iconPackagingBlk} alt="icon of packaging" />
                            <p className='termini__infos'>Sicura antiurto</p>
                        </div>
                        <div className="delevery-termini">
                            <img src={iconPagSecuroBlk} alt="icon of paiement secure" />
                            <p className='termini__infos'>100% Sicuro</p>
                        </div>
                        <div className="delevery-termini mb-12">
                            <img src={iconDeliveryBlk} alt="icon of delais for delivery" />
                            <p className='termini__infos'>Consegnato il: {deliveryDate.toLocaleDateString('it-IT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Cart;
